import { Component } from "react";
import { PrismBanner, PrismButton } from "@prism-ui/react";
import './ErrorBoundary.css';

class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { 
      hasError: false,
      error: null,
      errorInfo: null,
      serverError: null,
      networkError: null,
      detailsOpen: false,
      copied: false,
    };
    
    // Bind event handlers
    this.handleServerError = this.handleServerError.bind(this);
    this.handleNetworkError = this.handleNetworkError.bind(this);
  }

  static getDerivedStateFromError(error) {
    // Update state so the next render will show the fallback UI.
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    // Log error to console
    console.error('ErrorBoundary caught an error:', error, errorInfo);
    
    this.setState({
      error,
      errorInfo
    });
    
    // You can also log the error to an error reporting service
    // logErrorToMyService(error, errorInfo);
  }

  componentDidMount() {
    // Listen for custom server error events from axios interceptor
    window.addEventListener('server-error', this.handleServerError);
    window.addEventListener('server-unreachable', this.handleNetworkError);
  }

  componentWillUnmount() {
    window.removeEventListener('server-error', this.handleServerError);
    window.removeEventListener('server-unreachable', this.handleNetworkError);
  }

  handleServerError(event) {
    this.setState({
      serverError: event.detail
    });
    
    // Auto-clear after 10 seconds
    setTimeout(() => {
      this.setState({ serverError: null });
    }, 10000);
  }

  handleNetworkError(event) {
    this.setState({
      networkError: event.detail
    });
    
    // Auto-clear after 10 seconds (user might have fixed connection)
    setTimeout(() => {
      this.setState({ networkError: null });
    }, 10000);
  }

  handleReload = () => {
    window.location.reload();
  };

  render() {
    const isDevelopment = process.env.NODE_ENV === 'development';
    
    // Show network error banner (non-blocking)
    const networkErrorBanner = this.state.networkError && (
      <PrismBanner
        tone="danger"
        dismissible
        onPrismClick={() => this.setState({ networkError: null })}
        className="error-banner"
      >
        <h4 className="error-banner-heading">
          <i className="bi bi-wifi-off"></i> Connection Error
        </h4>
        <p>{this.state.networkError.message}</p>
        {isDevelopment && this.state.networkError.code && (
          <small className="clr:text-secondary-base">Error Code: {this.state.networkError.code}</small>
        )}
      </PrismBanner>
    );
    
    // Show server error banner (non-blocking)
    const serverErrorBanner = this.state.serverError && (
      <PrismBanner
        tone="caution"
        dismissible
        onPrismClick={() => this.setState({ serverError: null })}
        className="error-banner"
      >
        <h4 className="error-banner-heading">
          <i className="bi bi-exclamation-triangle"></i> Server Error
        </h4>
        <p>{this.state.serverError.message}</p>
        {isDevelopment && this.state.serverError.status && (
          <small className="clr:text-secondary-base">
            Status: {this.state.serverError.status}
            {this.state.serverError.url && ` | URL: ${this.state.serverError.url}`}
          </small>
        )}
      </PrismBanner>
    );

    // Show critical error page (blocking)
    if (this.state.hasError) {
      const { error, errorInfo, detailsOpen, copied } = this.state;

      const handleCopyToCopilot = () => {
        const lines = [
          '## React Error Report',
          '',
          `**Error:** ${error?.toString() || 'Unknown error'}`,
          '',
        ];
        if (errorInfo?.componentStack) {
          lines.push('**Component Stack:**', '```', errorInfo.componentStack.trim(), '```', '');
        }
        if (error?.stack) {
          // Filter to only app source lines — exclude React/Vite/node_modules internals
          const appLines = error.stack
            .split('\n')
            .filter(l => !l.includes('node_modules'))
            .map(l => l
              .replace(/https?:\/\/localhost:\d+\//, '')  // strip origin
              .replace(/\?t=\d+/, '')                     // strip Vite cache-busters
            )
            .join('\n')
            .trim();
          if (appLines) {
            lines.push('**Stack Trace (app frames only):**', '```', appLines, '```', '');
          }
        }
        lines.push('Please help me diagnose and fix this error.');
        navigator.clipboard.writeText(lines.join('\n')).then(() => {
          this.setState({ copied: true });
          setTimeout(() => this.setState({ copied: false }), 2000);
        });
      };

      return (
        <div className="error-boundary-container">
          <PrismBanner tone="danger" priority="low" className="error-alert">
            <h4 className="error-banner-heading">
              <i className="bi bi-exclamation-circle"></i> Something went wrong
            </h4>
            <p>
              We're sorry, but something unexpected happened. Please try reloading the page.
            </p>
            
            {isDevelopment && error && (
              <details
                className="error-details"
                onToggle={e => this.setState({ detailsOpen: e.target.open })}
              >
                <summary>Error Details (Development Only)</summary>
                <pre className="error-stack">
                  {error.toString()}
                  {errorInfo?.componentStack}
                </pre>
              </details>
            )}
            
            <hr />
            <div className="error-boundary-actions">
              <div className="error-boundary-actions-primary">
                <PrismButton tone="primary" onPrismClick={this.handleReload}>
                  <i className="bi bi-arrow-clockwise"></i> Reload Page
                </PrismButton>
                <PrismButton
                  tone="secondary"
                  display="outline"
                  onPrismClick={() => window.location.href = '/'}
                >
                  <i className="bi bi-house"></i> Go Home
                </PrismButton>
              </div>
              {isDevelopment && detailsOpen && (
                <PrismButton
                  tone="secondary"
                  display="outline"
                  size="sm"
                  className="error-copy-btn"
                  onPrismClick={handleCopyToCopilot}
                  title="Copy error details formatted for GitHub Copilot"
                >
                  <i className={`bi ${copied ? 'bi-check2' : 'bi-clipboard'}`}></i>
                  {copied ? ' Copied!' : ' Copy to Copilot'}
                </PrismButton>
              )}
            </div>
          </PrismBanner>
        </div>
      );
    }

    return (
      <>
        {networkErrorBanner}
        {serverErrorBanner}
        {this.props.children}
      </>
    );
  }
}

export default ErrorBoundary;